import React, { useState } from 'react';
import { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import useForm, { Validations } from 'src/hooks/useForm';
import {
  addOrEditTaskGroup,
  setIsTaskGroupModalOpen,
  setSelectedTaskGroupTableRow,
  TaskGroupTableDataRow,
} from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { MAX_TASK_NAME_CHAR_LENGTH } from 'src/store/adminConfigPageSlice/common/constants';
import TaskGroupModalForm from './taskGroupModalForm';

// **** Constants **** //

const VALIDATIONS: Validations<TaskGroupTableDataRow> = {
  label: {
    required: {
      value: true,
      message: 'Group name is required',
    },
    custom: {
      isValid: (value) => value.length <= MAX_TASK_NAME_CHAR_LENGTH,
      message: `Group name must be less than or equal to ${MAX_TASK_NAME_CHAR_LENGTH} characters.`,
    },
  },
  region: {
    required: {
      value: true,
      message: 'Region is required',
    },
  },
};

// **** Components **** //

/**
 * TaskGroupModal Content
 */
const TaskGroupModalContent = () => {
  const dispatch = useAppDispatch();
  const { selectedTaskGroupTableRow } = useAppSelector((state) => state.taskConfigPage);
  const [showSpinner, setShowSpinner] = useState(false);

  // Handle the task-group form
  const form = useForm<TaskGroupTableDataRow>({
    validations: { ...VALIDATIONS },
    initialValues: {
      label: selectedTaskGroupTableRow?.label ?? '',
      region: selectedTaskGroupTableRow?.region ?? '',
      tasks: selectedTaskGroupTableRow?.tasks ?? [],
    },
  });

  // addOrEdit TaskGroup
  const onSave = async () => {
    if (form.validateForm()) {
      setShowSpinner(true);
      await dispatch(addOrEditTaskGroup(form.formData));
      setShowSpinner(false);
      dispatch(setIsTaskGroupModalOpen(false));
      dispatch(setSelectedTaskGroupTableRow(null));
      form.resetForm();
    }
  };

  // Close modal
  const onCancel = () => {
    dispatch(setIsTaskGroupModalOpen(false));
    dispatch(setSelectedTaskGroupTableRow(null));
    form.resetForm();
  };

  return (
    <>
      <LoadingSpinnerWrapper showSpinner={showSpinner}>
        <TaskGroupModalForm form={form} />
      </LoadingSpinnerWrapper>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fill">
          <Button disabled={showSpinner} type="secondary" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={showSpinner} type="primary" size="small" onClick={onSave}>
            Save
          </Button>
        </Row>
      </ModalFooter>
    </>
  );
};

// **** Export **** //

export default TaskGroupModalContent;
